import {getTestProps} from '@benchling/js-test-utils';
import {Alert, Box, Button, Group, Stack, Title} from '@mantine/core';
import React, {useState} from 'react';

import DataConnector from 'connect/DataConnector';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {getInsufficientCreditsAgentTypeError} from 'llm/chat-library/MeteringUtils';
import {getScientificAIDisabledMessage} from 'model-hub/permissions/ModelHubPermissions';
import {NO_MODELS_INSTALLED_BANNER_MESSAGE} from 'model-hub/scientific-models/NoModelsInstalledEmptyState';
import type {ScientificModelNode} from 'model-hub/scientific-models/ScientificModelMetadata';
import {useScientificModelRegistry} from 'model-hub/scientific-models/useAvailableScientificModels';
import notifications from 'notifications';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import Menu from 'ui-library/menus/Menu';
import {MenuWidth} from 'ui-library/menus/MenuConstants';
import Option from 'ui-library/menus/Option';
import {CenteredSpinner} from 'ui-library/Spinner';
import {withLoadingScreen} from 'util/Promises';

import type {MoleculeFileQuery, MoleculeFileQueryVariables} from './__generated/MoleculeFileQuery';
import {MOLECULE_FILE_QUERY} from './MoleculeFileState';
import {
  getMoleculePropertyPredictionModels,
  requestPropertyPrediction,
  resolvePropertyPredictionFolderId,
} from './MoleculePredictedPropertiesTabUtils';

export interface ConnectedMoleculePredictedPropertiesTabProps {
  moleculeVersionId: string;
}

export interface UnconnectedMoleculePredictedPropertiesTabProps {
  moleculeId: string;
}

export type MoleculePredictedPropertiesTabProps = ConnectedMoleculePredictedPropertiesTabProps &
  UnconnectedMoleculePredictedPropertiesTabProps;

class MoleculePredictedPropertiesTabConnector extends DataConnector<
  UnconnectedMoleculePredictedPropertiesTabProps,
  ConnectedMoleculePredictedPropertiesTabProps
> {
  override readonly reduxSubscriptionPaths = ['ui.chemistry.moleculeVersionUIs'];

  enhanceProps(): ConnectedMoleculePredictedPropertiesTabProps {
    const moleculeVersionId =
      this.reduxState().ui.chemistry.moleculeVersionUIs[this.props.moleculeId].moleculeVersionId;
    return {moleculeVersionId};
  }
}

function MoleculePredictedPropertiesTab({moleculeId}: MoleculePredictedPropertiesTabProps): JSX.Element {
  return (
    <Box
      className="react-tab-container"
      role="region"
      aria-label="Property Prediction"
      {...getTestProps({
        component: 'MoleculePredictedPropertiesTab',
        element: 'root',
      })}
    >
      <Stack spacing="md">
        <Title order={2}>Property Prediction</Title>
        <MoleculePredictedPropertiesTabContent moleculeId={moleculeId} />
      </Stack>
    </Box>
  );
}

function MoleculePredictedPropertiesTabContent({moleculeId}: {moleculeId: string}): JSX.Element {
  const registryResult = useScientificModelRegistry();
  const moleculeQueryResult = useBenchlingQuery<MoleculeFileQuery, MoleculeFileQueryVariables>(
    MOLECULE_FILE_QUERY,
    {variables: {fileId: moleculeId}, fetchPolicy: 'cache-only'}
  );

  if (registryResult.type === 'loading' || moleculeQueryResult.type === QueryResultType.PENDING) {
    return <CenteredSpinner />;
  }
  if (registryResult.type === 'error' || moleculeQueryResult.type === QueryResultType.ERROR) {
    return (
      <Alert color="red" role="alert">
        Unable to load models. Please refresh the page.
      </Alert>
    );
  }

  const molecule = moleculeQueryResult.data.molecule;
  if (molecule == null || isInaccessible(molecule)) {
    return (
      <Alert color="red" role="alert">
        You do not have access to view this molecule. Please ask your administrator for access.
      </Alert>
    );
  }

  return (
    <LoadedMoleculePredictedPropertiesTab
      availableModels={getMoleculePropertyPredictionModels(registryResult.registry.availableModels)}
      moleculeFolderId={molecule.folder.id}
      moleculeId={moleculeId}
    />
  );
}

function LoadedMoleculePredictedPropertiesTab({
  availableModels,
  moleculeFolderId,
  moleculeId,
}: {
  availableModels: Array<ScientificModelNode>;
  moleculeFolderId: string;
  moleculeId: string;
}): JSX.Element {
  const [selectedModelReference, setSelectedModelReference] = useState<string | null>(
    availableModels[0]?.externalReference ?? null
  );
  const [creditError, setCreditError] = useState<{title: string; detail: string} | null>(null);

  const selectedModel =
    availableModels.find((model) => model.externalReference === selectedModelReference) ?? null;

  const blockingMessage =
    getScientificAIDisabledMessage() ??
    (availableModels.length === 0 ? NO_MODELS_INSTALLED_BANNER_MESSAGE : undefined);
  const canRun = selectedModel != null && blockingMessage == null;

  const handleRun = async (): Promise<void> => {
    if (selectedModel == null || blockingMessage != null) {
      return;
    }
    setCreditError(null);
    try {
      await requestPropertyPrediction({
        folderId: resolvePropertyPredictionFolderId(moleculeFolderId),
        model: selectedModel,
        moleculeId,
      });
      notifications.success(`${selectedModel.displayName} run created.`);
    } catch (error) {
      const creditsErr = getInsufficientCreditsAgentTypeError(error);
      if (creditsErr != null) {
        setCreditError(creditsErr);
      } else {
        throw error;
      }
    }
  };

  return (
    <Stack spacing="sm">
      {blockingMessage != null ? (
        <Alert color="yellow" role="alert">
          {blockingMessage}
        </Alert>
      ) : null}
      {creditError != null ? (
        <Alert color="red" role="alert" title={creditError.title}>
          {creditError.detail}
        </Alert>
      ) : null}
      <Group align="flex-end" spacing="sm" grow={true} noWrap={true}>
        {availableModels.length > 0 ? (
          <Box
            {...getTestProps({
              component: 'MoleculePredictedPropertiesTab',
              element: 'modelSelect',
            })}
          >
            <SelectDropdown
              displayName={selectedModel?.displayName ?? 'Select a model'}
              menu={
                <Menu width={MenuWidth.FULL}>
                  {availableModels.map(({externalReference, displayName}) => (
                    <Option
                      key={externalReference}
                      value={displayName}
                      onSelect={() => {
                        setSelectedModelReference(externalReference);
                      }}
                      isSelected={selectedModelReference === externalReference}
                    >
                      {displayName}
                    </Option>
                  ))}
                </Menu>
              }
              disabled={blockingMessage != null}
            />
          </Box>
        ) : null}
        <Button
          variant="primary"
          disabled={!canRun}
          leftIcon={<BenchlingIcon icon="sparkle" />}
          onClick={() => withLoadingScreen(handleRun())}
        >
          Create new run
        </Button>
      </Group>
    </Stack>
  );
}

const ConnectedMoleculePredictedPropertiesTab = MoleculePredictedPropertiesTabConnector.connect(
  MoleculePredictedPropertiesTab
);
export default ConnectedMoleculePredictedPropertiesTab;
